import React, { useState, useEffect, useCallback } from "react";
import { Button, Placeholder, Text } from "@telegram-apps/telegram-ui";
import ApiService from "../../handlers/apiService";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./Leaderboard.css";
import { UserRank } from "../../handlers/types";
import { retrieveLaunchParams } from "@telegram-apps/sdk";

export function Leaderboard() {
  const { t } = useI18n();
  const [topUsers, setTopUsers] = useState<UserRank[]>([]);
  const [currentUser, setCurrentUser] = useState<UserRank | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState<boolean>(false);

  const fetchLeaderboard = useCallback(
    async (isRefetch = false) => {
      if (isRefetch) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const api = ApiService.getInstance();

        // Get current user ID from Telegram launch params
        let currentUserId;
        try {
          const params = retrieveLaunchParams();
          currentUserId = params.tgWebAppData?.user?.id;
        } catch (err) {
          // Ignore error and proceed with leaderboard
        }

        const response = await api.api.usersLeaderboardList();

        // Format response based on the new API structure
        if (response && Array.isArray(response)) {
          const reversedResponse = [...response].reverse();

          // Find current user in the leaderboard data
          const currentUserData = currentUserId
            ? reversedResponse.find((user) => user.id === currentUserId)
            : undefined;

          setTopUsers(response.slice(0, 14) || []); // To cut current user if they outside of top 15

          // Create a CurrentUser object from the current user data
          if (currentUserData) {
            setCurrentUser({
              id: currentUserData.id,
              firstName: currentUserData.firstName,
              secondName: currentUserData.secondName || null,
              username: currentUserData.username || undefined,
              evl: currentUserData.evl,
              rank: currentUserData.rank,
            });
          } else {
            setCurrentUser(null);
          }
        } else {
          setTopUsers([]);
          setCurrentUser(null);
          setError(t("toolkit.leaderboard.errorLoadingData"));
        }
      } catch (err) {
        setError(t("toolkit.leaderboard.errorLoadingTryAgain"));
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    },
    [t]
  );

  const refetch = useCallback(() => fetchLeaderboard(true), [fetchLeaderboard]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  if (isLoading) {
    return (
      <Placeholder
        header={t("toolkit.leaderboard.loadingHeader")}
        description={t("toolkit.leaderboard.loadingDescription")}
      />
    );
  }

  if (error) {
    return (
      <Placeholder
        header={t("common.error")}
        description={error}
        action={
          <Button onClick={() => refetch()}>{t("common.tryAgain")}</Button>
        }
      />
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">{t("toolkit.leaderboard.title")}</h1>
        <p className="leaderboard-subtitle">
          {t("toolkit.leaderboard.subtitle")}
        </p>
      </div>

      {isRefetching && (
        <Text className="leaderboard-status">
          {t("toolkit.leaderboard.refreshing")}
        </Text>
      )}

      <div className="leaderboard-list">
        {topUsers.map((user, index) => (
          <div
            key={user.id}
            className={`leaderboard-item ${
              currentUser?.id === user.id ? "leaderboard-item--current" : ""
            }`}
          >
            <div className="leaderboard-rank">{index + 1}</div>
            <div className="leaderboard-user">
              <div className="leaderboard-user-info">
                <p className="leaderboard-user-name">
                  {user.firstName} {user.secondName || ""}
                </p>
                {user.username && (
                  <p className="leaderboard-user-username">@{user.username}</p>
                )}
              </div>
            </div>
            <div className="leaderboard-coins">
              <span className="leaderboard-coins-amount">
                                  {user.evl}
                  <img
                    src="/assets/img/ui/EVL.webp"
                    alt="EVL"
                    className="coin-icon"
                  />
              </span>
            </div>
          </div>
        ))}
      </div>

      {currentUser &&
        currentUser.rank &&
        currentUser.rank > topUsers.length && (
          <div className="current-user-section">
            <div className="current-user-divider">
              <div className="divider-line"></div>
              <Text className="divider-text">
                {t("toolkit.leaderboard.yourPosition")}
              </Text>
              <div className="divider-line"></div>
            </div>

            <div className="leaderboard-item leaderboard-item--current">
              <div className="leaderboard-rank">{currentUser.rank}</div>
              <div className="leaderboard-user">
                <div className="leaderboard-user-info">
                  <p className="leaderboard-user-name">
                    {currentUser.firstName} {currentUser.secondName || ""}
                  </p>
                  {currentUser.username && (
                    <p className="leaderboard-user-username">
                      @{currentUser.username}
                    </p>
                  )}
                </div>
              </div>
              <div className="leaderboard-coins">
                <span className="leaderboard-coins-amount">
                  {currentUser.evl}
                  <img
                    src="/assets/img/ui/EVL.webp"
                    alt="EVL"
                    className="coin-icon"
                  />
                </span>
              </div>
            </div>
          </div>
        )}

      {topUsers.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🏆</div>
          <Text className="empty-state-title">
            {t("toolkit.leaderboard.noRankingsYet")}
          </Text>
          <Text className="empty-state-message">
            {t("toolkit.leaderboard.beTheFirst")}
          </Text>
        </div>
      )}
    </div>
  );
}
